import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import FormProvider, { RHFSelect, RHFInput, RHFInputNumber, RHFCheckbox } from 'Components/Organisms/HookForm';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { INVOICE_SUBTYPES } from 'Utils/constants';
import Checkbox from 'Components/Atoms/Checkbox/Checkbox';
import Icon from 'Components/Atoms/Icon/Icon';
import useDebounce from 'Hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { getInvoiceSeriePreview } from 'Services/invoices';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';

function useGetInvoiceSeriePreview(params) {
    return useQuery({
        queryKey: ['invoice-serie-preview', params],
        queryFn: async () => {
            return getInvoiceSeriePreview(params);
        },
    });
}

export default function InvoiceSerieEditor(props) {
    const { onCancel, serie, onSubmit, enableFirstValueThisYear, onError } = props;

    const { t } = useTranslation('invoices');
    const [hasFirstValueThisYear, setHasFirstValueThisYear] = useState(
        () => typeof serie.first_value_this_year === 'number',
    );

    const serieSchema = yup.object().shape({
        name: yup.string().required(t('field-required')),
        invoice_subtype: yup.string().oneOf(Object.values(INVOICE_SUBTYPES)).required(t('field-required')),
        format: yup.string().required(t('field-required')),
        first_value: yup
            .number()
            .nullable()
            .transform((value, original) => (original === '' ? null : value))
            .min(0, t('field-required'))
            .required(t('field-required')),
        first_value_this_year: yup
            .number(t('field-required'))
            .nullable()
            .transform((value, original) => (original === '' ? null : value))
            .min(0, t('field-required'))
            .notRequired(),
    });

    const hasLastGenerationDate = !!serie.last_generation_date;

    const methods = useForm({
        resolver: yupResolver(serieSchema),
        defaultValues: {
            name: serie.name ?? '',
            invoice_subtype: serie.invoice_subtype ?? INVOICE_SUBTYPES.STANDARD,
            format: serie.format ?? 'F[YY]####',
            first_value: typeof serie.first_value === 'number' ? serie.first_value : 1,
            first_value_this_year: typeof serie.first_value_this_year === 'number' ? serie.first_value_this_year : '',
            is_restarted_yearly: serie.is_restarted_yearly ?? false,
            is_default: serie.is_default ?? false,
        },
    });

    const {
        handleSubmit,
        setError,
        watch,
        formState: { isSubmitting, errors },
    } = methods;

    const format = watch('format');
    const firstValue = watch('first_value');
    const firstValueThisYear = watch('first_value_this_year');

    const seriePreviewParmas = useMemo(() => {
        const firstValueThisYearNumber = typeof firstValueThisYear === 'number' ? firstValueThisYear : null;

        return {
            format,
            first_value: firstValue !== '' ? firstValue : 1,
            first_value_this_year: enableFirstValueThisYear && hasFirstValueThisYear ? firstValueThisYearNumber : null,
        };
    }, [format, firstValue, firstValueThisYear, enableFirstValueThisYear, hasFirstValueThisYear]);

    const debouncedSeriePreviewParams = useDebounce(seriePreviewParmas, 1000);
    const { data = '', isLoading, isError } = useGetInvoiceSeriePreview(debouncedSeriePreviewParams);
    const typeOptions = useMemo(
        () => [
            { value: INVOICE_SUBTYPES.STANDARD, label: t('invoice-standard') },
            { value: INVOICE_SUBTYPES.STANDARD_CORRECTIVE, label: t('invoice-standard-corrective') },
            { value: INVOICE_SUBTYPES.SIMPLIFIED, label: t('invoice-simplified') },
            { value: INVOICE_SUBTYPES.SIMPLIFIED_CORRECTIVE, label: t('invoice-simplified-corrective') },
            { value: INVOICE_SUBTYPES.QUOTE, label: t('invoice-quote') },
            { value: INVOICE_SUBTYPES.PROFORMA, label: t('invoice-proforma') },
            { value: INVOICE_SUBTYPES.DELIVERY_NOTE, label: t('invoice-delivery-note') },
        ],
        [t],
    );

    const submit = async (data) => {
        const firstValueThisYear =
            hasFirstValueThisYear && typeof data.first_value_this_year === 'number' ? data.first_value_this_year : null;

        try {
            await getInvoiceSeriePreview({
                format: data.format,
                first_value: data.first_value !== '' ? data.first_value : 1,
                first_value_this_year: enableFirstValueThisYear && hasFirstValueThisYear ? firstValueThisYear : null,
            });
        } catch {
            setError('format', { type: 'manual', message: t('serie-invalid-format') });
            return;
        }

        const normalizedBody = {
            name: data.name,
            invoice_subtype: data.invoice_subtype,
            format: data.format,
            first_value: data.first_value,
            is_restarted_yearly: data.is_restarted_yearly,
            is_default: data.is_default,
            ...(enableFirstValueThisYear && {
                first_value_this_year: firstValueThisYear,
            }),
        };

        try {
            await onSubmit(normalizedBody);
        } catch (error) {
            if (error?.response?.data?.description === 'serie_invalid_format') {
                setError('format', { type: 'manual', message: t('serie-invalid-format') });
            } else if (error?.response?.data?.description === 'format_already_exists') {
                setError('format', { type: 'manual', message: t('serie-format-already-exists') });
            } else if (error?.response?.data?.description === 'invalid_first_value') {
                setError('first_value', { type: 'manual', message: t('serie-invalid-first-value') });
            } else if (error?.response?.data?.description === 'invalid_format') {
                setError('format', { type: 'manual', message: t('serie-invalid-format') });
            } else if (error?.response?.data?.description === 'invalid_first_value_this_year_max_size') {
                setError('first_value_this_year', {
                    type: 'manual',
                    message: t('serie-invalid-first-value-this-year'),
                });
            } else {
                onError(error, {
                    title: serie.id ? t('error-updating-serie-title') : t('error-creating-serie-title'),
                    description: serie.id
                        ? t('error-updating-serie-description')
                        : t('error-creating-serie-description'),
                });
            }
        }
    };

    return (
        <FormProvider className="flex flex-col h-full" methods={methods} onSubmit={handleSubmit(submit)}>
            {hasLastGenerationDate && (
                <div className="text-dark-blue flex items-start gap-3 rounded-md p-3.5 bg-warning/20 mb-4 text-sm">
                    <Icon name="circle-alert" size={20} color="currentColor" className="shrink-0" />
                    <span>{t('serie-edit-warning')}</span>
                </div>
            )}

            <div className="grow pb-6 pt-6">
                <div className="grid gap-5">
                    <RHFInput className="w-full" name="name" data-autofocus="true" label={t('name')} />
                    <RHFSelect
                        name="invoice_subtype"
                        className="w-full"
                        label={t('invoice-type')}
                        options={typeOptions}
                        disabled={hasLastGenerationDate}
                    />
                    <div className="pb-4">
                        <RHFInput
                            className="w-full"
                            name="format"
                            label={t('format')}
                            placeholder={t('format-placeholder')}
                            disabled={hasLastGenerationDate}
                        />
                        <div className="py-2 text-xs px-0.5">
                            {!errors.format && (
                                <>
                                    {isError && <div className="mb-2 text-danger">{t('serie-preview-error')}</div>}
                                    {!isError && (
                                        <div className="mb-2  text-dark-blue flex items-center gap-1">
                                            {t('example-with-the-indicated-format')}
                                            {isLoading ? <Skeleton width={60} height={14} /> : <span>{data}</span>}
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="text-gray-500 text-pretty">{t('format-description')}</div>
                        </div>
                    </div>
                    <RHFInputNumber
                        className="w-full"
                        name="first_value"
                        label={t('first-serie-number')}
                        min={0}
                        step={1}
                        disabled={hasLastGenerationDate}
                    />

                    <div className="grid gap-3.5 pb-3">
                        <RHFCheckbox className="text-sm" name="is_restarted_yearly">
                            {t('restart-yearly')}
                        </RHFCheckbox>
                        {enableFirstValueThisYear && (
                            <>
                                <Checkbox
                                    className="text-sm"
                                    checked={hasFirstValueThisYear}
                                    onChange={(ev) => setHasFirstValueThisYear(ev.target.checked)}
                                >
                                    {t('starting-number-this-year')}
                                </Checkbox>
                                {hasFirstValueThisYear && (
                                    <div className="pt-1.5 pb-3">
                                        <RHFInputNumber
                                            className="w-full"
                                            name="first_value_this_year"
                                            label={t('start-by')}
                                            min={0}
                                            step={1}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        <RHFCheckbox className="text-sm" name="is_default">
                            {t('default-serie')}
                        </RHFCheckbox>
                    </div>
                </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button variant="primary" isBusy={isSubmitting} type="submit" className="grow">
                        {t('save')}
                    </Button>
                    <Button className="grow" onClick={onCancel}>
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

InvoiceSerieEditor.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    serie: PropTypes.object.isRequired,
    onError: PropTypes.func.isRequired,
    enableFirstValueThisYear: PropTypes.bool,
};
